import SettingsPageLayout, { SettingsPageLoading, settingsPageTitleClass } from "../../../components/shared/SettingsPageLayout";
import React from "react";
import Checkbox from "@mui/material/Checkbox";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { toast } from "react-toastify";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import ConfirmDialog from "../boards/ConfirmDialog";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_assign_store_to_group, API_delete_store_group, API_save_store_group } from "../../../constants";

// Settings > Store groups. Regions on the left, districts on the right; a
// district may sit under a region, and a location may sit in either. The
// parent page fetches the payload (every group plus every open store in the
// company) and refetches after each save through `onChanged`. Editing happens
// in a right-hand drawer, like the location drawer on Manage Locations.

const KIND_LABEL = { region: "Region", district: "District" };
const NONE = "";

const byName = (a, b) => (a.name || a.store_name || "").localeCompare(b.name || b.store_name || "");

// The drawer draft survives the Settings shell unmounting this view (another
// tab, then back) the same way the comparison view's inline drafts do.
const draftKey = (companyId) => `storeGroupsEditor:${companyId}`;
const readDraft = (companyId) => {
  try {
    const raw = sessionStorage.getItem(draftKey(companyId));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};
const writeDraft = (companyId, editor) => {
  try {
    if (editor) sessionStorage.setItem(draftKey(companyId), JSON.stringify(editor));
    else sessionStorage.removeItem(draftKey(companyId));
  } catch (_) {
    // Storage can be unavailable; the draft is a convenience, not the record.
  }
};

// What a draft compares against to know whether it has unsaved changes.
const snapshot = (editor) =>
  JSON.stringify({ name: editor.name, parent_uuid: editor.parent_uuid, store_ids: [...editor.store_ids].sort(), district_uuids: [...editor.district_uuids].sort() });
const withSnapshot = (editor) => ({ ...editor, initial: snapshot(editor) });
const isDirty = (editor) => Boolean(editor) && snapshot(editor) !== editor.initial;

// A restored draft may predate membership changes made elsewhere (the
// location drawer, another tab). Re-apply only what the user added or
// removed on top of the group's current membership, so a save made to finish
// a name edit cannot silently undo an assignment saved meanwhile.
const rebase = (draftList, initialList, currentList) => {
  const added = draftList.filter((x) => !initialList.includes(x));
  const removed = initialList.filter((x) => !draftList.includes(x));
  return [...currentList.filter((x) => !removed.includes(x)), ...added.filter((x) => !currentList.includes(x))];
};
const rebaseDraft = (draft, currentStoreIds, currentDistrictUuids) => {
  let initial;
  try {
    initial = JSON.parse(draft.initial);
  } catch (_) {
    return draft;
  }
  const next = {
    ...draft,
    store_ids: rebase(draft.store_ids, initial.store_ids || [], currentStoreIds),
    district_uuids: rebase(draft.district_uuids, initial.district_uuids || [], currentDistrictUuids),
  };
  return {
    ...next,
    initial: snapshot({ ...next, name: initial.name, parent_uuid: initial.parent_uuid, store_ids: currentStoreIds, district_uuids: currentDistrictUuids }),
  };
};

const inputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  padding: "5px",
  height: "45px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};
const checkboxSx = { color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, py: 0.5 };

export default function StoreGroupsDisplay({ companyId, payload, isAdmin, onChanged }) {
  const groups = React.useMemo(() => (payload?.groups || []).slice().sort(byName), [payload]);
  const stores = React.useMemo(() => (payload?.stores || []).slice().sort(byName), [payload]);
  const regions = groups.filter((group) => group.kind === "region");
  const districts = groups.filter((group) => group.kind === "district");
  const districtsOf = (regionUuid) => districts.filter((district) => district.parent_uuid === regionUuid);
  const storesOf = (groupUuid) => stores.filter((store) => store.store_group_uuid === groupUuid);
  const unassigned = stores.filter((store) => !store.store_group_uuid);
  const groupByUuid = (uuid) => groups.find((group) => group.uuid === uuid);
  const regionStoreCount = (region) =>
    storesOf(region.uuid).length + districtsOf(region.uuid).reduce((n, district) => n + storesOf(district.uuid).length, 0);
  const groupLabel = (group) => {
    if (group.kind === "region") return `${group.name} (region)`;
    const region = group.parent_uuid ? groupByUuid(group.parent_uuid) : null;
    return region ? `${group.name} · ${region.name}` : group.name;
  };

  const [editor, setEditor] = React.useState(() => readDraft(companyId));
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  // A draft restored from storage is rebased onto the first payload seen.
  const needsRebase = React.useRef(Boolean(editor));
  React.useEffect(() => {
    if (!needsRebase.current || !payload) return;
    needsRebase.current = false;
    setEditor((draft) => {
      if (!draft || !draft.uuid) return draft;
      const currentStores = (payload.stores || []).filter((s) => s.store_group_uuid === draft.uuid).map((s) => s.id);
      const currentDistricts = (payload.groups || []).filter((g) => g.parent_uuid === draft.uuid).map((g) => g.uuid);
      return rebaseDraft(draft, currentStores, currentDistricts);
    });
  }, [payload]);
  // Not reported to the Settings shell as dirty: the drawer is modal, its
  // draft survives in sessionStorage, and closing with changes asks first.
  // The shell's "Unsaved changes" line would only add a header row behind
  // the drawer and shift the page.
  React.useEffect(() => {
    writeDraft(companyId, editor);
  }, [companyId, editor]);

  const openCreate = (kind, parentUuid = null) =>
    setEditor(withSnapshot({ uuid: null, name: "", kind, parent_uuid: parentUuid, store_ids: [], district_uuids: [] }));
  const openEdit = (group) =>
    setEditor(withSnapshot({
      uuid: group.uuid,
      name: group.name,
      kind: group.kind,
      parent_uuid: group.parent_uuid,
      store_ids: storesOf(group.uuid).map((store) => store.id),
      district_uuids: districtsOf(group.uuid).map((district) => district.uuid),
    }));
  // Closing with unsaved changes asks first; a save closes without asking.
  const closeEditor = () => setEditor(null);
  const dismissEditor = () => {
    if (isDirty(editor) && !window.confirm("Discard unsaved changes to this group?")) return;
    closeEditor();
  };

  const request = async (url, body, successMessage) => {
    setSaving(true);
    toast.loading("Saving store groups...", { autoClose: false });
    try {
      await postRightworkAPI(url, { company_id: companyId, ...body });
      toast.dismiss();
      toast.success(successMessage, { autoClose: 3000 });
      await onChanged?.();
      return true;
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.system?.message ?? "Request Failed", { autoClose: 3000 });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveEditor = async () => {
    if (!editor) return;
    const ok = await request(
      API_save_store_group,
      {
        store_group_uuid: editor.uuid,
        name: editor.name,
        kind: editor.kind,
        parent_uuid: editor.kind === "district" ? editor.parent_uuid : null,
        store_ids: editor.store_ids,
        ...(editor.kind === "region" ? { district_uuids: editor.district_uuids } : {}),
      },
      `${KIND_LABEL[editor.kind]} saved`
    );
    if (ok) closeEditor();
  };

  // "Not in a group" bucket: put one store straight into a region or district.
  // A single-store call, so a stale copy of the group's members can never
  // detach a location someone else added meanwhile.
  const assignUnassigned = async (store, groupUuid) => {
    const group = groupByUuid(groupUuid);
    if (!group) return;
    const ok = await request(
      API_assign_store_to_group,
      { store_id: store.id, store_group_uuid: group.uuid },
      `${store.store_name} added to ${group.name}`
    );
    // The drawer holds its own copy of the group's locations; if it has this
    // group open, its next Save must not undo the assignment.
    if (ok) {
      setEditor((prev) =>
        prev && prev.uuid === group.uuid && !prev.store_ids.includes(store.id)
          ? { ...prev, store_ids: [...prev.store_ids, store.id] }
          : prev
      );
    }
  };

  // Districts column: put one top-level district under a region.
  const assignDistrictToRegion = async (district, regionUuid) => {
    const region = groupByUuid(regionUuid);
    if (!region) return;
    const ok = await request(
      API_save_store_group,
      { store_group_uuid: district.uuid, name: district.name, kind: district.kind, parent_uuid: region.uuid },
      `${district.name} added to ${region.name}`
    );
    if (ok) {
      setEditor((prev) =>
        prev && prev.uuid === region.uuid && !prev.district_uuids.includes(district.uuid)
          ? { ...prev, district_uuids: [...prev.district_uuids, district.uuid] }
          : prev
      );
    }
  };

  const confirmDelete = async () => {
    const group = pendingDelete;
    setPendingDelete(null);
    const ok = await request(API_delete_store_group, { store_group_uuid: group.uuid }, `${KIND_LABEL[group.kind]} deleted`);
    if (ok && editor?.uuid === group.uuid) closeEditor();
  };

  const toggleEditorStore = (storeId) =>
    setEditor((prev) => ({
      ...prev,
      store_ids: prev.store_ids.includes(storeId) ? prev.store_ids.filter((id) => id !== storeId) : [...prev.store_ids, storeId],
    }));
  const toggleEditorDistrict = (districtUuid) =>
    setEditor((prev) => ({
      ...prev,
      district_uuids: prev.district_uuids.includes(districtUuid)
        ? prev.district_uuids.filter((uuid) => uuid !== districtUuid)
        : [...prev.district_uuids, districtUuid],
    }));

  if (!isAdmin) {
    return (
      <SettingsPageLayout>
        <h1 className={settingsPageTitleClass}>Store groups</h1>
        <p className="mt-6 text-sm text-slate-500">Only company administrators can manage store groups.</p>
      </SettingsPageLayout>
    );
  }
  if (payload?.error) {
    return (
      <SettingsPageLayout>
        <h1 className={settingsPageTitleClass}>Store groups</h1>
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center">
          <div className="text-sm font-semibold text-slate-700">Store groups could not be loaded</div>
          <button type="button" onClick={() => onChanged?.()} className="mt-2 text-sm font-semibold text-violet-700 hover:text-violet-800">Try again</button>
        </div>
      </SettingsPageLayout>
    );
  }
  if (!payload) {
    return <SettingsPageLoading />;
  }

  const deleteMessage = pendingDelete?.kind === "region"
    ? `${pendingDelete.name} will be removed. Its districts and locations stay, without a region.`
    : `${pendingDelete?.name} will be removed. Its locations stay, without a district.`;

  const storeChips = (list, emptyText) => (
    <div className="flex flex-wrap gap-1.5">
      {list.map((store) => (
        <span key={store.id} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">
          <StoreOutlinedIcon style={{ fontSize: 12 }} className="text-slate-400" />{store.store_name}
        </span>
      ))}
      {list.length === 0 && emptyText && <span className="text-xs text-slate-400">{emptyText}</span>}
    </div>
  );

  const addButton = (label, onClick, primary) => (
    <button
      type="button"
      onClick={onClick}
      className={primary
        ? "flex h-9 items-center gap-1 rounded-lg bg-violet-700 px-3 text-sm font-medium text-white hover:bg-violet-800"
        : "flex h-9 items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"}
    >
      <AddIcon fontSize="small" /> {label}
    </button>
  );

  // One row per district: inside its region's card, or in the "without a
  // region" section with an inline region select.
  const districtRow = (district, { regionSelect = false } = {}) => (
    <div key={district.uuid} className="border-b border-slate-100 px-4 py-3 last:border-b-0">
      <div className="flex items-center gap-2.5">
        <span className="text-sm font-medium text-slate-900">{district.name}</span>
        <span className="text-xs text-slate-500">{storesOf(district.uuid).length} locations</span>
        <span className="flex-1" />
        {regionSelect && regions.length > 0 && (
          <div className="w-48">
            <label id={`region-for-${district.uuid}-label`} htmlFor={`region-for-${district.uuid}`} className="sr-only">Region for {district.name}</label>
            <RightworkSelect
              id={`region-for-${district.uuid}`}
              value={NONE}
              disabled={saving}
              onChange={(event) => assignDistrictToRegion(district, event.target.value)}
              options={[
                { value: NONE, label: "Choose a region", disabled: true },
                ...regions.map((item) => ({ value: item.uuid, label: item.name })),
              ]}
            />
          </div>
        )}
        <button type="button" onClick={() => openEdit(district)} className="text-xs font-medium text-violet-700 hover:underline">Edit</button>
      </div>
      <div className="mt-2">{storeChips(storesOf(district.uuid), "No locations yet")}</div>
    </div>
  );

  const editorTitle = editor
    ? editor.uuid ? `Edit ${editor.name || KIND_LABEL[editor.kind].toLowerCase()}` : `New ${KIND_LABEL[editor.kind].toLowerCase()}`
    : "";

  const topLevelDistricts = districtsOf(null);

  return (
    <SettingsPageLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className={settingsPageTitleClass}>Store groups</h1>
          <p className="mt-1 text-sm text-slate-500">Organize locations into regions and districts.</p>
        </div>
        <div className="flex items-center gap-2">
          {addButton("Add district", () => openCreate("district"), false)}
          {addButton("Add region", () => openCreate("region"), true)}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {groups.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
            No groups yet. Add a region, then districts under it, or start with either on its own.
          </div>
        )}

        {regions.map((region) => {
          const children = districtsOf(region.uuid);
          const direct = storesOf(region.uuid);
          return (
            <section key={region.uuid} aria-label={`${region.name} region`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center gap-2.5 border-b border-slate-200 bg-slate-50 px-4 py-3">
                <span className="text-sm font-semibold text-slate-900">{region.name}</span>
                <span className="rounded bg-violet-100 px-1.5 py-px text-[11px] font-semibold uppercase tracking-wider text-violet-700">Region</span>
                <span className="text-xs text-slate-500">{children.length} districts · {regionStoreCount(region)} locations</span>
                <span className="flex-1" />
                <button type="button" onClick={() => openCreate("district", region.uuid)} className="flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline">
                  <AddIcon style={{ fontSize: 14 }} />New district
                </button>
                <button type="button" onClick={() => openEdit(region)} className="text-xs font-medium text-violet-700 hover:underline">Edit</button>
              </div>
              {children.map((district) => districtRow(district))}
              {(direct.length > 0 || children.length === 0) && (
                <div className="px-4 py-3">
                  {direct.length > 0 && <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Locations in the region directly</p>}
                  {storeChips(direct, "No districts or locations yet. Edit the region to add them.")}
                </div>
              )}
            </section>
          );
        })}

        {topLevelDistricts.length > 0 && (
          <section aria-label="Districts without a region" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center gap-2.5 border-b border-slate-200 bg-slate-50 px-4 py-3">
              <span className="text-sm font-semibold text-slate-900">Districts without a region</span>
              <span className="text-xs text-slate-500">{topLevelDistricts.length} districts</span>
            </div>
            {topLevelDistricts.map((district) => districtRow(district, { regionSelect: true }))}
          </section>
        )}

        {unassigned.length > 0 && (
          <section aria-label="Not in a group" className="overflow-hidden rounded-xl border border-dashed border-slate-300 bg-white">
            <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
              <span className="text-sm font-semibold text-slate-900">Not in a group</span>
              <span className="text-xs text-slate-500">{unassigned.length} locations</span>
              <span className="flex-1" />
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-700">
                <WarningAmberOutlinedIcon style={{ fontSize: 14 }} />Left out of region and district targeting
              </span>
            </div>
            {unassigned.map((store) => (
              <div key={store.id} className="flex items-center gap-3 border-b border-slate-100 px-4 py-2.5 last:border-b-0">
                <StoreOutlinedIcon style={{ fontSize: 16 }} className="text-slate-400" />
                <span className="flex-1 text-sm text-slate-900">{store.store_name}</span>
                <div className="w-64">
                  <label id={`assign-${store.id}-label`} htmlFor={`assign-${store.id}`} className="sr-only">Group for {store.store_name}</label>
                  <RightworkSelect
                    id={`assign-${store.id}`}
                    value={NONE}
                    disabled={saving || groups.length === 0}
                    onChange={(event) => assignUnassigned(store, event.target.value)}
                    options={[
                      { value: NONE, label: groups.length ? "Choose a region or district" : "Add a region or district first", disabled: true },
                      ...regions.map((group) => ({ value: group.uuid, label: groupLabel(group) })),
                      ...districts.map((group) => ({ value: group.uuid, label: groupLabel(group) })),
                    ]}
                  />
                </div>
              </div>
            ))}
          </section>
        )}
      </div>

      {/* Edit drawer, the same shell as the location drawer on Manage Locations. */}
      <Drawer open={Boolean(editor)} anchor="right" onClose={dismissEditor}>
        {editor && (
          <section aria-label={editorTitle} className="relative h-full w-[450px] max-w-full">
            <div className="max-h-screen overflow-auto">
              <DrawerHeader title={editorTitle} onClose={dismissEditor} />
              <div className="w-full px-6">
                <div className="mt-2">
                  <p className="text-sm text-slate-500">{KIND_LABEL[editor.kind]} name</p>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ "aria-label": `${KIND_LABEL[editor.kind]} name` }}
                      value={editor.name}
                      onChange={(event) => setEditor((prev) => ({ ...prev, name: event.target.value }))}
                      InputProps={{ sx: inputSx }}
                    />
                  </div>
                </div>

                {editor.kind === "district" && (
                  <div className="mt-5">
                    <label id="store-group-parent-label" htmlFor="store-group-parent" className="text-sm text-slate-500">Region</label>
                    <div className="mt-1">
                      <RightworkSelect
                        id="store-group-parent"
                        value={editor.parent_uuid ?? NONE}
                        onChange={(event) => setEditor((prev) => ({ ...prev, parent_uuid: event.target.value === NONE ? null : event.target.value }))}
                        options={[{ value: NONE, label: "No region" }, ...regions.map((region) => ({ value: region.uuid, label: region.name }))]}
                      />
                    </div>
                  </div>
                )}

                {editor.kind === "region" && (
                  <div className="mt-5">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Districts in this region</p>
                      <span className="text-xs text-slate-500">{editor.district_uuids.length} of {districts.length}</span>
                    </div>
                    <div className="mt-1 max-h-[240px] overflow-y-auto rounded-lg border border-slate-200">
                      {districts.length === 0 && <div className="px-3 py-4 text-center text-sm text-slate-500">No districts yet.</div>}
                      {districts.map((district) => {
                        const checked = editor.district_uuids.includes(district.uuid);
                        const current = district.parent_uuid && district.parent_uuid !== editor.uuid ? groupByUuid(district.parent_uuid) : null;
                        return (
                          <label key={district.uuid} className="flex min-h-[42px] cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50">
                            <Checkbox checked={checked} onChange={() => toggleEditorDistrict(district.uuid)} size="small" inputProps={{ "aria-label": district.name }} sx={checkboxSx} />
                            <span className={`flex-1 text-sm ${checked ? "font-medium text-violet-800" : "text-slate-700"}`}>{district.name}</span>
                            {!checked && <span className="text-[11px] text-slate-500">{current ? `Moves from ${current.name}` : district.parent_uuid ? "" : "No region"}</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">Locations in this {KIND_LABEL[editor.kind].toLowerCase()}</p>
                    <span className="text-xs text-slate-500">{editor.store_ids.length} of {stores.length}</span>
                  </div>
                  <div className="mt-1 max-h-[320px] overflow-y-auto rounded-lg border border-slate-200">
                    {stores.length === 0 && <div className="px-3 py-4 text-center text-sm text-slate-500">No locations in this company.</div>}
                    {stores.map((store) => {
                      const checked = editor.store_ids.includes(store.id);
                      const current = store.store_group_uuid && store.store_group_uuid !== editor.uuid ? groupByUuid(store.store_group_uuid) : null;
                      return (
                        <label key={store.id} className="flex min-h-[42px] cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50">
                          <Checkbox checked={checked} onChange={() => toggleEditorStore(store.id)} size="small" inputProps={{ "aria-label": store.store_name }} sx={checkboxSx} />
                          <StoreOutlinedIcon style={{ fontSize: 16 }} className="text-slate-400" />
                          <span className={`flex-1 text-sm ${checked ? "font-medium text-violet-800" : "text-slate-700"}`}>{store.store_name}</span>
                          {!checked && <span className="text-[11px] text-slate-500">{current ? `Moves from ${current.name}` : store.store_group_uuid ? "" : "Not in a group"}</span>}
                        </label>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    {editor.kind === "district" ? "Locations you untick leave this district." : "Locations you untick leave this region; districts you untick keep their locations."}
                  </p>
                </div>

                {editor.uuid && (
                  <div className="mt-5">
                    <button type="button" onClick={() => setPendingDelete(groupByUuid(editor.uuid))} className="text-sm font-medium text-rose-600 hover:underline">
                      Delete {KIND_LABEL[editor.kind].toLowerCase()}
                    </button>
                  </div>
                )}
              </div>
              <div className="h-[200px]" />
            </div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <LoadingButton
                  variant="contained"
                  loading={saving}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  disabled={!editor.name.trim()}
                  onClick={saveEditor}
                  sx={{ width: "100%", textTransform: "none", borderRadius: "12px", paddingY: "14px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" }, fontSize: "16px" }}
                >
                  Save {KIND_LABEL[editor.kind].toLowerCase()}
                </LoadingButton>
              </div>
              <div className="mt-3 w-full">
                <ButtonOutlined text="Cancel" onClick={dismissEditor} />
              </div>
            </DrawerFooterContainerLarge>
          </section>
        )}
      </Drawer>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : ""}
        message={pendingDelete ? deleteMessage : ""}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </SettingsPageLayout>
  );
}
